// components/LoginModal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { app } from "../config/firebaseConfig";
import { createUserProfile, checkUserExists } from '../services/firestoreService';
import { getAuth, GoogleAuthProvider, sendSignInLinkToEmail, signInWithPopup } from "firebase/auth";
import Modal from './Modal';
import { TbCheck } from "react-icons/tb";

const auth = getAuth(app);

const LoginModal = ({ isOpen, closeModal, redirect = null }) => {
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && isOpen) {
      if (redirect) {
        navigate(redirect);
      }
      closeModal();
    }
  }, [user, isOpen, redirect, navigate, closeModal]);

  const displayError = (message) => {
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage("");
    }, 5000);
  };

  const handleEmailLinkSignup = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      displayError("Please enter a valid email address.");
      return;
    }

    setIsEmailSending(true);
    const actionCodeSettings = {
      url: process.env.REACT_APP_FINISH_SIGN_UP_URL || `${window.location.origin}/finish-sign-up`,
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      setEmailSent(true);
    } catch (error) {
      console.error("Error sending sign-in link: ", error);
      displayError(error.message);
    } finally {
      setIsEmailSending(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    setIsGoogleLoading(true);
    setErrorMessage("");

    try {
      const result = await signInWithPopup(auth, provider);
      const { user: googleUser } = result;
      const userExists = await checkUserExists(googleUser.uid);

      if (!userExists) {
        await createUserProfile(googleUser.uid, googleUser.email, googleUser.displayName, "Free");
      }
      if (redirect) {
        navigate(redirect);
      }
      closeModal();
    } catch (error) {
      console.error("Error signing in with Google:", error);
      if (error.code !== 'auth/popup-closed-by-user') {
        displayError(error.message || "Failed to sign in with Google.");
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} closeModal={closeModal}>
      <div className="flex flex-col items-center justify-center p-6 sm:p-8 pt-2">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Sign in to your account</h1>
          <p className="text-sm text-gray-500 mt-1">Instant 1-click access to create & save your resumes.</p>
        </div>

        {errorMessage && (
          <div className="w-full p-3 mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl" role="alert">
            {errorMessage}
          </div>
        )}

        {/* Prominent Google Button */}
        <button
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
          className="w-full py-3 px-4 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-xl shadow-sm hover:shadow transition-all font-semibold flex items-center justify-center gap-3 disabled:opacity-60 text-base"
        >
          {isGoogleLoading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-gray-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Signing in...</span>
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5">
                <path fill="#4285F4" d="M44.5 20H24v8.5h11.8C33.4 33.8 29 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.6 1.1 7.6 2.9l5.8-5.8C34.1 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c10.6 0 19.3-8.1 19.9-18.5H44.5z"/>
                <path fill="#34A853" d="M24 38c4.7 0 8.6-1.5 11.5-4.1l-5.5-4.1c-1.6 1.1-3.6 1.8-6 1.8-4.6 0-8.5-3.1-9.9-7.4H7.6v4.7C10.4 32.9 16.8 38 24 38z"/>
                <path fill="#FBBC05" d="M13.9 24c-.4-1.2-.6-2.5-.6-3.8s.2-2.6.6-3.8V11.7H7.6C5.9 15.3 5 19.5 5 24s.9 8.7 2.6 12.3l6.3-4.7z"/>
                <path fill="#EA4335" d="M24 10c2.6 0 4.9.9 6.7 2.4l5.1-5.1C32.6 5.2 28.5 4 24 4c-4.6 0-8.9 1.4-12.4 4.1l6.3 4.7C19.5 11.4 21.6 10 24 10z"/>
              </svg>
              <span>Continue with Google</span>
            </>
          )}
        </button>

        <div className="w-full flex items-center my-5">
          <div className="flex-grow border-t border-gray-200"></div>
          <span className="mx-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Or</span>
          <div className="flex-grow border-t border-gray-200"></div>
        </div>

        {emailSent ? (
          <div className="w-full p-4 bg-green-50 border border-green-200 rounded-xl text-center">
            <div className="w-9 h-9 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-1.5">
              <TbCheck size={18} />
            </div>
            <h4 className="text-sm font-bold text-green-900">Magic Link Sent!</h4>
            <p className="text-xs text-green-700 mt-1">
              Check your email <strong>{email}</strong> and click the link to log in instantly.
            </p>
            <button
              type="button"
              onClick={() => setEmailSent(false)}
              className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={handleEmailLinkSignup} className="w-full space-y-3">
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button 
              type="submit" 
              disabled={isEmailSending}
              className="w-full py-2.5 px-4 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm disabled:opacity-60"
            >
              {isEmailSending ? 'Sending link...' : 'Email Sign-in Link'}
            </button>
            <p className="text-center text-[11px] text-gray-400">
              No password needed. Click the link sent to your email.
            </p>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default LoginModal;
